/**
 * Branch-aware canvas layout for a CloudSync runbook (positionX / positionY) plus waypoint routing.
 *
 * A pure module: it takes the runbook's `steps` (the shape generateWorkflowJson produces) and returns
 * positions and waypoints. It never touches React Flow state, so it can run in a Web Worker.
 *
 * Rules
 *  - Flow runs top to bottom; steps are layered by the longest path from the start.
 *  - If/Else: True goes right of False (the portal draws True from the right handle, False from the left).
 *  - Loops are boxes: the For Each on top, the body below it, the End Loop at the bottom of the body.
 *  - Line crossings are reduced (barycentre ordering, then a greedy sideways refine on the straight-line picture).
 *  - Long lines get waypoints routed through the free bands between layers, on the 12 px grid.
 *
 * This is a port of tools/runbook-layout (Python), which stays the reference until both agree on the golden
 * fixtures in runbookLayout.test.js.
 */

export const EFFORTS = ['fast', 'normal'];

const ACTION_IF_ELSE = 6;
const ACTION_FOR_EACH = 8;
const ACTION_END_LOOP = 17;

const EDGE_FIELDS = [
  ['nextStepId', 'n'],
  ['conditionTruePath', 'T'],
  ['conditionFalsePath', 'F'],
  ['errorStepId', 'E'],
];
const EDGE_KEYS = [
  ['conditionTruePath', 'true'],
  ['conditionFalsePath', 'false'],
  ['nextStepId', 'next'],
];

export const GRID = 12; // the portal snaps waypoints to a 12 px grid
const SIDE_WEIGHT = 2.5; // a True branch left of its False branch costs this many line crossings
const DUMMY_W = 70;
const ROUTE_MARGIN = 30; // clear space kept between a routed line and any step

export const DEFAULT_CONFIG = { nodeW: 300, nodeH: 70, gapX: 60, gapY: 60 };

const cmp = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
const sum = (xs) => xs.reduce((a, b) => a + b, 0);
// Round half to even, like Python's round(), so the grid snap matches the reference tool exactly.
const roundHalfEven = (v) => {
  const r = Math.round(v);
  return Math.abs(v % 1) === 0.5 && r % 2 !== 0 ? r - 1 : r;
};
const snap = (v) => roundHalfEven(v / GRID) * GRID;

function pushTo(map, key, value) {
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
}

// --------------------------------------------------------------------------------------------------
// layered layout
// --------------------------------------------------------------------------------------------------
class Layout {
  constructor(steps, cfg) {
    this.cfg = cfg;
    this.steps = new Map(steps.map((s) => [s.stepNumber, s]));
    this.kids = new Map();
    steps.forEach((s) => pushTo(this.kids, s.parentStepId || '', s.stepNumber));
    this.loops = new Set([...this.steps].filter(([, s]) => s.action === ACTION_FOR_EACH).map(([k]) => k));
    this.endloop = new Map();
    steps.forEach((s) => {
      if (s.action === ACTION_END_LOOP) this.endloop.set(s.parentStepId || '', s.stepNumber);
    });
    this.pos = new Map();
  }

  outEdges(u, scopeNodes) {
    const s = this.steps.get(u);
    const edges = [];
    if (this.loops.has(u)) {
      const el = this.endloop.get(u);
      if (el) {
        const nxt = this.steps.get(el).nextStepId;
        if (nxt) edges.push([nxt, 'n']);
      }
    } else if (s.action !== ACTION_END_LOOP) {
      EDGE_FIELDS.forEach(([f, lab]) => {
        if (s[f]) edges.push([s[f], lab]);
      });
    }
    return edges.filter(([v]) => scopeNodes.has(v));
  }

  layoutScope(scope) {
    const { nodeW, nodeH, gapY } = this.cfg;
    const endHere = scope ? this.endloop.get(scope) : undefined;
    const nodes = (this.kids.get(scope) || []).filter(
      (n) => !(this.steps.get(n).action === ACTION_END_LOOP && scope !== '' && n === this.endloop.get(scope))
    );
    const allNodes = new Set(nodes);
    if (endHere) allNodes.add(endHere);
    const inner = new Map();
    nodes.forEach((n) => {
      if (this.loops.has(n)) inner.set(n, this.layoutScope(n));
    });
    const dims = new Map();
    allNodes.forEach((n) => {
      if (inner.has(n)) {
        const body = inner.get(n);
        const half = Math.max(body.entryCx, body.w - body.entryCx, nodeW / 2);
        dims.set(n, [2 * half, nodeH + gapY + body.h]);
      } else {
        dims.set(n, [nodeW, nodeH]);
      }
    });
    let E = [];
    nodes.forEach((u) => {
      this.outEdges(u, allNodes).forEach(([v, lab]) => E.push([u, v, lab]));
    });
    const entry = this.entryOf(scope, allNodes, E);
    E = this.keepPrimaryEdges(E, endHere);
    const layer = this.layering(allNodes, E, entry, endHere);
    const { order, dummies, chain } = this.orderLayers(layer, E);
    const xs = this.assignX(order, dims, dummies, chain);
    const { ys, totalH } = this.assignY(order, dims, dummies);
    const rel = new Map();
    order.forEach((items, lyr) => {
      items.forEach((it) => {
        if (dummies.has(it)) return;
        const [w] = dims.get(it);
        rel.set(it, [xs.get(it) - w / 2, ys.get(lyr)]);
      });
    });
    let minx = Infinity;
    let maxx = -Infinity;
    rel.forEach(([x], n) => {
      minx = Math.min(minx, x);
      maxx = Math.max(maxx, x + dims.get(n)[0]);
    });
    if (!rel.size) {
      minx = 0;
      maxx = nodeW;
    }
    rel.forEach(([x, y], n) => rel.set(n, [x - minx, y]));
    const width = Math.max(maxx - minx, nodeW);
    let ecx = width / 2;
    if (rel.has(entry)) ecx = rel.get(entry)[0] + dims.get(entry)[0] / 2;
    return { rel, dims, inner, w: width, h: totalH, entry, entryCx: ecx };
  }

  /** A join node keeps only the edge from its earliest-created parent; the others become back edges. */
  keepPrimaryEdges(E, endHere) {
    const byTarget = new Map();
    E.forEach(([u, v, lab]) => pushTo(byTarget, v, [this.steps.get(u).stepOrder, u, v, lab]));
    const keep = [];
    byTarget.forEach((lst, v) => {
      if (v === endHere || lst.length === 1) {
        lst.forEach(([, u, vv, lab]) => keep.push([u, vv, lab]));
      } else {
        lst.sort((a, b) => a[0] - b[0] || cmp(a[1], b[1]) || cmp(a[2], b[2]) || cmp(a[3], b[3]));
        const [, u, vv, lab] = lst[0];
        keep.push([u, vv, lab]);
      }
    });
    return keep;
  }

  entryOf(scope, allNodes, E) {
    if (scope) {
      const first = this.steps.get(scope).nextStepId;
      if (allNodes.has(first)) return first;
    }
    const indeg = new Map();
    E.forEach(([, v]) => indeg.set(v, (indeg.get(v) || 0) + 1));
    const byOrder = [...allNodes].sort((a, b) => this.steps.get(a).stepOrder - this.steps.get(b).stepOrder);
    const cands = byOrder.filter((n) => !indeg.get(n) && n !== this.endloop.get(scope));
    return cands.length ? cands[0] : [...allNodes].sort(cmp)[0];
  }

  layering(nodes, E, entry, endHere) {
    const out = new Map();
    const indeg = new Map();
    E.forEach(([u, v]) => {
      pushTo(out, u, v);
      indeg.set(v, (indeg.get(v) || 0) + 1);
    });
    const orderNodes = [...nodes].sort((a, b) => this.steps.get(a).stepOrder - this.steps.get(b).stepOrder);
    const queue = orderNodes.filter((n) => !indeg.get(n) && n !== endHere);
    const layer = new Map(queue.map((n) => [n, 0]));
    const indeg2 = new Map(indeg);
    while (queue.length) {
      const u = queue.shift();
      (out.get(u) || []).forEach((v) => {
        layer.set(v, Math.max(layer.get(v) || 0, layer.get(u) + 1));
        indeg2.set(v, indeg2.get(v) - 1);
        if (indeg2.get(v) === 0) queue.push(v);
      });
    }
    nodes.forEach((n) => {
      if (!layer.has(n)) layer.set(n, 0);
    });
    if (endHere) {
      const others = [...nodes].filter((n) => n !== endHere).map((n) => layer.get(n));
      layer.set(endHere, (others.length ? Math.max(...others) : -1) + 1);
    }
    let changed = true;
    while (changed) {
      changed = false;
      orderNodes.forEach((u) => {
        const kids = out.get(u);
        if (u === endHere || !kids || !kids.length) return;
        const tgt = Math.min(...kids.map((v) => layer.get(v)));
        if (tgt - 1 > layer.get(u) && !indeg.get(u) && u !== entry) {
          layer.set(u, tgt - 1);
          changed = true;
        }
      });
    }
    return layer;
  }

  orderLayers(layer, E) {
    const dummies = new Set();
    const chain = [];
    let counter = 0;
    const hops = new Map(); // `${step}|T` -> first node after the If/Else edge, a dummy when the edge is long
    E.forEach(([u, v, lab]) => {
      const lu = layer.get(u);
      const lv = layer.get(v);
      if (lv <= lu) return;
      let prev = u;
      let first = v;
      for (let l = lu + 1; l < lv; l += 1) {
        const d = `~${counter}`;
        counter += 1;
        dummies.add(d);
        layer.set(d, l);
        if (prev === u) first = d;
        chain.push([prev, d]);
        prev = d;
      }
      chain.push([prev, v]);
      if (lab === 'T' || lab === 'F') hops.set(`${u}|${lab}`, first);
    });
    const sides = [];
    hops.forEach((tHop, key) => {
      if (!key.endsWith('|T')) return;
      const fHop = hops.get(`${key.slice(0, -2)}|F`);
      if (fHop !== undefined && tHop !== fHop) sides.push([tHop, fHop]);
    });
    const sideRank = new Map();
    sides.forEach(([t, f]) => {
      sideRank.set(f, -1);
      sideRank.set(t, 1);
    });
    const layers = new Map();
    layer.forEach((l, n) => pushTo(layers, l, n));
    const up = new Map();
    const down = new Map();
    chain.forEach(([a, b]) => {
      pushTo(down, a, b);
      pushTo(up, b, a);
    });
    const key0 = (n) => (this.steps.has(n) ? this.steps.get(n).stepOrder : 1e9);
    layers.forEach((lst) => lst.sort((a, b) => key0(a) - key0(b) || cmp(a, b)));

    const layerKeys = [...layers.keys()];
    const lmin = Math.min(...layerKeys);
    const lmax = Math.max(...layerKeys);

    const crossings = (lo) => {
      let total = 0;
      for (let l = lmin; l < lmax; l += 1) {
        const here = lo.get(l);
        const next = lo.get(l + 1) || [];
        const nextIdx = new Map(next.map((n, i) => [n, i]));
        const pairs = [];
        here.forEach((a, i) => {
          (down.get(a) || []).forEach((b) => {
            if (nextIdx.has(b)) pairs.push([i, nextIdx.get(b)]);
          });
        });
        for (let i = 0; i < pairs.length; i += 1) {
          for (let j = i + 1; j < pairs.length; j += 1) {
            if ((pairs[i][0] - pairs[j][0]) * (pairs[i][1] - pairs[j][1]) < 0) total += 1;
          }
        }
      }
      return total;
    };
    const wrongSide = (lo) => {
      let n = 0;
      sides.forEach(([t, f]) => {
        const row = lo.get(layer.get(t));
        const it = row.indexOf(t);
        const iff = row.indexOf(f);
        if (it >= 0 && iff >= 0 && it < iff) n += 1;
      });
      return n;
    };
    const objective = (lo) => crossings(lo) + SIDE_WEIGHT * wrongSide(lo);
    const copy = (lo) => new Map([...lo].map(([l, v]) => [l, [...v]]));

    let best = copy(layers);
    let bestC = objective(best);
    const cur = copy(layers);
    for (let it = 0; it < 40; it += 1) {
      const range = [];
      if (it % 2 === 0) for (let l = lmin + 1; l <= lmax; l += 1) range.push(l);
      else for (let l = lmax - 1; l >= lmin; l -= 1) range.push(l);
      const nb = it % 2 === 0 ? up : down;
      range.forEach((l) => {
        const ref = it % 2 === 0 ? l - 1 : l + 1;
        const idx = new Map(cur.get(ref).map((n, i) => [n, i]));
        const snapIdx = new Map(cur.get(l).map((n, i) => [n, i]));
        const bary = (n) => {
          const ns = (nb.get(n) || []).filter((m) => idx.has(m)).map((m) => idx.get(m));
          return ns.length ? sum(ns) / ns.length : snapIdx.get(n);
        };
        const keyed = cur.get(l).map((n) => [Math.round(bary(n) * 1e6) / 1e6, sideRank.get(n) || 0, snapIdx.get(n), n]);
        keyed.sort((a, b) => a[0] - b[0] || a[1] - b[1] || a[2] - b[2]);
        cur.set(l, keyed.map((k) => k[3]));
      });
      let improved = true;
      let passes = 0;
      while (improved && passes < 3) {
        improved = false;
        passes += 1;
        for (let l = lmin; l <= lmax; l += 1) {
          const row = cur.get(l);
          for (let i = 0; i < row.length - 1; i += 1) {
            const before = objective(cur);
            [row[i], row[i + 1]] = [row[i + 1], row[i]];
            if (objective(cur) < before) improved = true;
            else [row[i], row[i + 1]] = [row[i + 1], row[i]];
          }
        }
      }
      const c = objective(cur);
      if (c < bestC) {
        bestC = c;
        best = copy(cur);
      }
    }
    return { order: best, dummies, chain };
  }

  assignX(order, dims, dummies, chain) {
    const { gapX } = this.cfg;
    const width = (n) => (dummies.has(n) ? DUMMY_W : dims.get(n)[0]);
    const up = new Map();
    const down = new Map();
    chain.forEach(([a, b]) => {
      pushTo(down, a, b);
      pushTo(up, b, a);
    });
    const x = new Map();
    const keys = [...order.keys()].sort((a, b) => a - b);
    keys.forEach((l) => {
      let cursor = 0;
      order.get(l).forEach((n) => {
        const w = width(n);
        x.set(n, cursor + w / 2);
        cursor += w + gapX;
      });
    });
    const makeBlock = (nodes, want) => {
      const total = sum(nodes.map(width)) + gapX * (nodes.length - 1);
      const offs = [];
      let cur = -total / 2;
      nodes.forEach((n) => {
        const w = width(n);
        offs.push(cur + w / 2);
        cur += w + gapX;
      });
      const pos = sum(nodes.map((n, i) => want.get(n) - offs[i])) / nodes.length;
      return { nodes, pos, w: total };
    };
    const resolve = (layerNodes, want) => {
      const blocks = [];
      layerNodes.forEach((n) => {
        blocks.push(makeBlock([n], want));
        while (blocks.length > 1) {
          const a = blocks[blocks.length - 2];
          const b = blocks[blocks.length - 1];
          if (a.pos + a.w / 2 + gapX > b.pos - b.w / 2 + 1e-6) {
            blocks.splice(blocks.length - 2, 2, makeBlock([...a.nodes, ...b.nodes], want));
          } else break;
        }
      });
      const res = new Map();
      blocks.forEach((b) => {
        let cur = b.pos - b.w / 2;
        b.nodes.forEach((m) => {
          const w = width(m);
          res.set(m, cur + w / 2);
          cur += w + gapX;
        });
      });
      return res;
    };
    const wanted = (nodes, nb) => {
      const want = new Map();
      nodes.forEach((n) => {
        const ms = nb.get(n);
        want.set(n, ms && ms.length ? sum(ms.map((m) => x.get(m))) / ms.length : x.get(n));
      });
      return want;
    };
    for (let i = 0; i < 60; i += 1) {
      keys.slice(1).forEach((l) => resolve(order.get(l), wanted(order.get(l), up)).forEach((v, n) => x.set(n, v)));
      [...keys].reverse().slice(0, -1).forEach((l) => resolve(order.get(l), wanted(order.get(l), down)).forEach((v, n) => x.set(n, v)));
    }
    return x;
  }

  assignY(order, dims, dummies) {
    const { nodeH, gapY } = this.cfg;
    const ys = new Map();
    let y = 0;
    [...order.keys()].sort((a, b) => a - b).forEach((l) => {
      ys.set(l, y);
      const heights = order.get(l).filter((n) => !dummies.has(n)).map((n) => dims.get(n)[1]);
      y += (heights.length ? Math.max(...heights) : nodeH) + gapY;
    });
    return { ys, totalH: y - gapY };
  }

  place(scopeResult, ox, oy) {
    const { nodeW, nodeH, gapY } = this.cfg;
    const { rel, dims, inner } = scopeResult;
    rel.forEach(([rx, ry], n) => {
      const [w] = dims.get(n);
      const ax = ox + rx;
      const ay = oy + ry;
      this.pos.set(n, [ax + (w - nodeW) / 2, ay]);
      if (inner.has(n)) {
        const body = inner.get(n);
        this.place(body, ax + w / 2 - body.entryCx, ay + nodeH + gapY);
      }
    });
  }

  run() {
    this.place(this.layoutScope(''), 0, 0);
    return this.pos;
  }
}

// --------------------------------------------------------------------------------------------------
// geometry and metrics
// --------------------------------------------------------------------------------------------------
const ccw = (a, b, c) => (c[1] - a[1]) * (b[0] - a[0]) - (b[1] - a[1]) * (c[0] - a[0]);

/** Liang-Barsky: does the segment a-b pass through the box? */
export function segHitsBox(a, b, bx, by, w, h) {
  const [x0, y0] = a;
  const dx = b[0] - x0;
  const dy = b[1] - y0;
  let t0 = 0;
  let t1 = 1;
  const checks = [
    [-dx, x0 - bx],
    [dx, bx + w - x0],
    [-dy, y0 - by],
    [dy, by + h - y0],
  ];
  for (let i = 0; i < checks.length; i += 1) {
    const [p, q] = checks[i];
    if (p === 0) {
      if (q < 0) return false;
    } else {
      const r = q / p;
      if (p < 0) {
        if (r > t1) return false;
        t0 = Math.max(t0, r);
      } else {
        if (r < t0) return false;
        t1 = Math.min(t1, r);
      }
    }
  }
  return (t1 - t0 > 0.02 && t1 - t0 < 0.98) || (t0 < 0.98 && t1 > 0.02 && t1 - t0 > 0.02);
}

function straightSegments(steps, pos, cfg) {
  const segs = [];
  steps.forEach((s) => {
    const u = s.stepNumber;
    EDGE_FIELDS.forEach(([f]) => {
      const v = s[f];
      if (!v || !pos.has(v) || !pos.has(u)) return;
      const a = [pos.get(u)[0] + cfg.nodeW / 2, pos.get(u)[1] + cfg.nodeH];
      const b = [pos.get(v)[0] + cfg.nodeW / 2, pos.get(v)[1]];
      segs.push([u, v, a, b]);
    });
  });
  return segs;
}

function crosses(s1, s2) {
  const [u1, v1, a, b] = s1;
  const [u2, v2, c, d] = s2;
  if (new Set([u1, v1, u2, v2]).size < 4) return false;
  return ccw(a, c, d) * ccw(b, c, d) < 0 && ccw(a, b, c) * ccw(a, b, d) < 0;
}

/** Measured on straight lines between the bottom-centre and top-centre anchors. */
export function metrics(steps, pos, config = {}) {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const segs = straightSegments(steps, pos, cfg);
  let lineCrossings = 0;
  for (let i = 0; i < segs.length; i += 1) {
    for (let j = i + 1; j < segs.length; j += 1) if (crosses(segs[i], segs[j])) lineCrossings += 1;
  }
  let through = 0;
  segs.forEach(([u, v, a, b]) => {
    for (const [k, [px, py]] of pos) {
      if (k === u || k === v) continue;
      let hit = false;
      for (let i = 1; i < 24 && !hit; i += 1) {
        const t = i / 24;
        const x = a[0] + (b[0] - a[0]) * t;
        const y = a[1] + (b[1] - a[1]) * t;
        if (px <= x && x <= px + cfg.nodeW && py <= y && y <= py + cfg.nodeH) hit = true;
      }
      if (hit) through += 1; // one per (line, step) pair, as the Python tool counts
    }
  });
  let overlaps = 0;
  const items = [...pos];
  for (let i = 0; i < items.length; i += 1) {
    for (let j = i + 1; j < items.length; j += 1) {
      const [, [x1, y1]] = items[i];
      const [, [x2, y2]] = items[j];
      if (Math.abs(x1 - x2) < cfg.nodeW && Math.abs(y1 - y2) < cfg.nodeH) overlaps += 1;
    }
  }
  const wrong = steps.filter((s) => {
    const t = s.conditionTruePath;
    const f = s.conditionFalsePath;
    return pos.has(t) && pos.has(f) && t !== f && pos.get(t)[0] < pos.get(f)[0] + 1;
  }).length;
  const xs = [...pos.values()].map((p) => p[0]);
  const ys = [...pos.values()].map((p) => p[1]);
  return {
    edges: segs.length,
    trueLeftOfFalse: wrong,
    lineCrossings,
    linesThroughNodes: through,
    overlappingNodes: overlaps,
    width: Math.round(Math.max(...xs) - Math.min(...xs) + cfg.nodeW),
    height: Math.round(Math.max(...ys) - Math.min(...ys) + cfg.nodeH),
  };
}

// --------------------------------------------------------------------------------------------------
// refine pass on the real straight-line picture
// --------------------------------------------------------------------------------------------------
class Refiner {
  constructor(steps, pos, cfg) {
    this.cfg = cfg;
    this.W = { cross: 15, through: 70, overlap: 1000, len: 0.02, up: 3, center: 0.005, chain: 0.1, side: 40 };
    this.steps = new Map(steps.map((s) => [s.stepNumber, s]));
    this.pos = new Map([...pos].map(([k, v]) => [k, [v[0], v[1]]]));
    this.cx = sum([...this.pos.values()].map((p) => p[0])) / this.pos.size;
    this.edges = [];
    steps.forEach((s) => {
      EDGE_FIELDS.forEach(([f]) => {
        const v = s[f];
        if (v && this.pos.has(v)) this.edges.push([s.stepNumber, v]);
      });
    });
    this.sides = [];
    steps.forEach((s) => {
      const t = s.conditionTruePath;
      const f = s.conditionFalsePath;
      if (this.pos.has(t) && this.pos.has(f) && t !== f) this.sides.push([t, f]);
    });
    this.inc = new Map();
    this.edges.forEach(([u, v], i) => {
      pushTo(this.inc, u, i);
      pushTo(this.inc, v, i);
    });
    const outd = new Map();
    const ind = new Map();
    this.edges.forEach(([u, v]) => {
      outd.set(u, (outd.get(u) || 0) + 1);
      ind.set(v, (ind.get(v) || 0) + 1);
    });
    this.chain = new Set();
    this.edges.forEach(([u, v], i) => {
      if (outd.get(u) === 1 && ind.get(v) === 1) this.chain.add(i);
    });
    const kids = new Map();
    steps.forEach((s) => pushTo(kids, s.parentStepId || '', s.stepNumber));
    const parentOf = new Map(steps.map((s) => [s.stepNumber, s.parentStepId || '']));
    this.frozen = new Set([...this.pos.keys()].filter((n) => parentOf.get(n) && parentOf.get(parentOf.get(n))));
    const collect = (n) => [n, ...(kids.get(n) || []).flatMap(collect)];
    this.desc = new Map();
    this.pos.forEach((_, n) => this.desc.set(n, this.steps.get(n).action === ACTION_FOR_EACH ? collect(n) : [n]));
  }

  anchors(i) {
    const { nodeW, nodeH } = this.cfg;
    const [u, v] = this.edges[i];
    const pu = this.pos.get(u);
    const pv = this.pos.get(v);
    return [
      [pu[0] + nodeW / 2, pu[1] + nodeH],
      [pv[0] + nodeW / 2, pv[1]],
    ];
  }

  edgeCross(i, j) {
    const [u1, v1] = this.edges[i];
    const [u2, v2] = this.edges[j];
    if (new Set([u1, v1, u2, v2]).size < 4) return 0;
    const [a, b] = this.anchors(i);
    const [c, d] = this.anchors(j);
    return ccw(a, c, d) * ccw(b, c, d) < 0 && ccw(a, b, c) * ccw(a, b, d) < 0 ? 1 : 0;
  }

  localCost(movedList) {
    const { nodeW, nodeH } = this.cfg;
    const W = this.W;
    const moved = new Set(movedList);
    const A = [...new Set([...moved].flatMap((n) => this.inc.get(n) || []))].sort((a, b) => a - b);
    const aSet = new Set(A);
    let cost = 0;
    A.forEach((i, idx) => {
      for (let k = idx + 1; k < A.length; k += 1) cost += W.cross * this.edgeCross(i, A[k]);
      for (let j = 0; j < this.edges.length; j += 1) if (!aSet.has(j)) cost += W.cross * this.edgeCross(i, j);
      const [a, b] = this.anchors(i);
      const [u, v] = this.edges[i];
      cost += (this.chain.has(i) ? W.chain : W.len) * (Math.abs(a[0] - b[0]) + Math.abs(b[1] - a[1]) * 0.1);
      if (b[1] < a[1] + 20) cost += W.up;
      this.pos.forEach((p, n) => {
        if (n !== u && n !== v && segHitsBox(a, b, p[0], p[1], nodeW, nodeH)) cost += W.through;
      });
    });
    for (let j = 0; j < this.edges.length; j += 1) {
      if (aSet.has(j)) continue;
      const [u, v] = this.edges[j];
      const [a, b] = this.anchors(j);
      moved.forEach((n) => {
        if (n !== u && n !== v) {
          const p = this.pos.get(n);
          if (segHitsBox(a, b, p[0], p[1], nodeW, nodeH)) cost += W.through;
        }
      });
    }
    moved.forEach((n) => {
      cost += W.center * Math.abs(this.pos.get(n)[0] + nodeW / 2 - this.cx);
    });
    this.sides.forEach(([t, f]) => {
      if ((moved.has(t) || moved.has(f)) && this.pos.get(t)[0] < this.pos.get(f)[0] + 1) cost += W.side;
    });
    const ml = [...moved].sort(cmp);
    ml.forEach((n) => {
      const p = this.pos.get(n);
      this.pos.forEach((q, m) => {
        if (m === n || (moved.has(m) && cmp(m, n) < 0)) return;
        if (Math.abs(p[0] - q[0]) < nodeW + 10 && Math.abs(p[1] - q[1]) < nodeH + 10) cost += W.overlap;
      });
    });
    return cost;
  }

  shift(group, dx) {
    group.forEach((n) => {
      this.pos.get(n)[0] += dx;
    });
  }

  run(passes = 14) {
    const order = [...this.pos.keys()]
      .filter((n) => !this.frozen.has(n))
      .sort((a, b) => this.steps.get(a).stepOrder - this.steps.get(b).stepOrder);
    const stepsDx = [-600, -480, -360, -240, -120, -60, 60, 120, 240, 360, 480, 600];
    for (let pass = 0; pass < passes; pass += 1) {
      let improved = 0;
      order.forEach((n) => {
        const group = this.desc.get(n);
        const base = this.localCost(group);
        let bestDx = 0;
        let bestDelta = 0;
        stepsDx.forEach((dx) => {
          this.shift(group, dx);
          const delta = this.localCost(group) - base;
          this.shift(group, -dx);
          if (delta < bestDelta - 1e-6) {
            bestDx = dx;
            bestDelta = delta;
          }
        });
        if (bestDx) {
          this.shift(group, bestDx);
          improved += 1;
        }
      });
      if (!improved) break;
    }
    return new Map([...this.pos].map(([k, v]) => [k, [v[0], v[1]]]));
  }
}

// --------------------------------------------------------------------------------------------------
// public layout API
// --------------------------------------------------------------------------------------------------
/** Shift the layout so the left-most step sits at marginX and the top step at y = 0. */
export function normalise(pos, marginX = 100) {
  const dx = marginX - Math.min(...[...pos.values()].map((p) => p[0]));
  const dy = -Math.min(...[...pos.values()].map((p) => p[1]));
  return new Map([...pos].map(([k, p]) => [k, [p[0] + dx, p[1] + dy]]));
}

/**
 * Positions for the runbook's steps: Map(stepNumber -> [x, y]).
 *  fast    layered layout only
 *  normal  layered layout, then a greedy sideways refine on the real straight-line picture
 * Deterministic for a given input and effort.
 */
export function computeLayout(steps, { effort = 'normal', ...config } = {}) {
  if (!EFFORTS.includes(effort)) throw new Error(`effort must be one of ${EFFORTS.join(', ')}`);
  const cfg = { ...DEFAULT_CONFIG, ...config };
  let pos = normalise(new Layout(steps, cfg).run());
  if (effort === 'fast') return pos;
  pos = normalise(new Refiner(steps, pos, cfg).run());
  return pos;
}

// --------------------------------------------------------------------------------------------------
// edge routing: waypoints so long lines pass beside the steps instead of through them
// --------------------------------------------------------------------------------------------------
/** [{from, to, key, points}] for every True / False / next edge; waypoints is Map(`${step}|${key}` -> [[x, y], ...]). */
export function edgePolylines(steps, pos, waypoints = new Map(), config = {}) {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const lines = [];
  steps.forEach((s) => {
    const u = s.stepNumber;
    EDGE_KEYS.forEach(([field, key]) => {
      const v = s[field];
      if (!v || !pos.has(v) || !pos.has(u)) return;
      const a = [pos.get(u)[0] + cfg.nodeW / 2, pos.get(u)[1] + cfg.nodeH];
      const b = [pos.get(v)[0] + cfg.nodeW / 2, pos.get(v)[1]];
      lines.push({ from: u, to: v, key, points: [a, ...(waypoints.get(`${u}|${key}`) || []), b] });
    });
  });
  return lines;
}

export function hitsAStep(points, pos, skip, pad = 0, config = {}) {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  for (let i = 0; i < points.length - 1; i += 1) {
    for (const [k, [x, y]] of pos) {
      if (!skip.includes(k) && segHitsBox(points[i], points[i + 1], x - pad, y - pad, cfg.nodeW + 2 * pad, cfg.nodeH + 2 * pad)) return true;
    }
  }
  return false;
}

function routeFor(u, v, pos, cfg) {
  const a = [pos.get(u)[0] + cfg.nodeW / 2, pos.get(u)[1] + cfg.nodeH];
  const b = [pos.get(v)[0] + cfg.nodeW / 2, pos.get(v)[1]];
  if (b[1] - a[1] < 2 * (cfg.nodeH + cfg.gapY)) return null;
  const y1 = snap(a[1] + cfg.gapY / 2);
  const y2 = snap(b[1] - cfg.gapY / 2);
  const band = [...pos].filter(([k, [, y]]) => k !== u && k !== v && y + cfg.nodeH > y1 && y < y2).map(([, p]) => p);
  const cands = new Set([snap(a[0]), snap(b[0])]);
  band.forEach(([x]) => {
    cands.add(snap(x - ROUTE_MARGIN));
    cands.add(snap(x + cfg.nodeW + ROUTE_MARGIN));
  });
  if (band.length) {
    cands.add(snap(Math.min(...band.map((p) => p[0])) - ROUTE_MARGIN - 2 * GRID));
    cands.add(snap(Math.max(...band.map((p) => p[0])) + cfg.nodeW + ROUTE_MARGIN + 2 * GRID));
  }
  // nearest lane first, the left one on a tie
  const ranked = [...cands].sort((p, q) => Math.abs(p - a[0]) + Math.abs(p - b[0]) - (Math.abs(q - a[0]) + Math.abs(q - b[0])) || p - q);
  for (const x of ranked) {
    if (!hitsAStep([a, [x, y1], [x, y2], b], pos, [u, v], ROUTE_MARGIN * 0.6, cfg)) {
      return [[x, y1], [x, y2]];
    }
  }
  return null;
}

/**
 * Waypoints for every edge whose straight line (or hand-placed route) touches a step.
 * Returns { routes, unresolved }: routes maps `${step}|${key}` to the points to use; unresolved lists edges that
 * still touch a step because no free corridor was found.
 */
export function routeLongEdges(steps, pos, existing = new Map(), config = {}) {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const routes = new Map();
  const unresolved = [];
  edgePolylines(steps, pos, existing, cfg).forEach(({ from: u, to: v, key, points }) => {
    const id = `${u}|${key}`;
    if (!hitsAStep(points, pos, [u, v], 0, cfg)) {
      if (existing.has(id)) routes.set(id, existing.get(id));
      return;
    }
    const r = routeFor(u, v, pos, cfg);
    const straight = [[pos.get(u)[0] + cfg.nodeW / 2, pos.get(u)[1] + cfg.nodeH], [pos.get(v)[0] + cfg.nodeW / 2, pos.get(v)[1]]];
    if (r) routes.set(id, r);
    else if (hitsAStep(straight, pos, [u, v], 0, cfg)) unresolved.push([u, key, v]);
    // else: a hand-placed waypoint made it worse; leave the edge straight
  });
  return { routes, unresolved };
}

/** Number of lines that pass through a step, counting each line as a polyline through its waypoints. */
export function countThrough(steps, pos, waypoints = new Map(), config = {}) {
  return edgePolylines(steps, pos, waypoints, config).filter(({ from, to, points }) => hitsAStep(points, pos, [from, to], 0, config)).length;
}

/** Map(`${step}|${key}` -> [[x, y], ...]) from the edgeWaypoints the portal stores on each step. */
export function readWaypoints(steps) {
  const out = new Map();
  steps.forEach((s) => {
    Object.entries(s.edgeWaypoints || {}).forEach(([key, pts]) => {
      if (pts && pts.length) out.set(`${s.stepNumber}|${key}`, pts.map((p) => [p.x, p.y]));
    });
  });
  return out;
}

/** The True / false / next waypoints of every step, in the portal's storage format ({ key: [{ x, y }] }). */
export function waypointsByStep(steps, routes) {
  const out = new Map();
  steps.forEach((s) => {
    const wp = {};
    EDGE_KEYS.forEach(([, key]) => {
      const pts = routes.get(`${s.stepNumber}|${key}`);
      if (pts && pts.length) wp[key] = pts.map(([x, y]) => ({ x: Math.round(x), y: Math.round(y) }));
    });
    if (Object.keys(wp).length) out.set(s.stepNumber, wp);
  });
  return out;
}

/** Layout and route in one call: { positions, waypoints, metrics }. */
export function formatRunbook(steps, options = {}) {
  const { keepWaypoints = false, ...layoutOptions } = options;
  const config = { ...DEFAULT_CONFIG, ...layoutOptions };
  if (keepWaypoints) {
    const positions = new Map(steps.map((s) => [s.stepNumber, [s.positionX, s.positionY]]));
    const existing = readWaypoints(steps);
    const { routes, unresolved } = routeLongEdges(steps, positions, existing, config);
    return { positions, routes, unresolved, throughBefore: countThrough(steps, positions, existing, config), throughAfter: countThrough(steps, positions, routes, config) };
  }
  const positions = computeLayout(steps, layoutOptions);
  const { routes, unresolved } = routeLongEdges(steps, positions, new Map(), config);
  return { positions, routes, unresolved, throughBefore: null, throughAfter: countThrough(steps, positions, routes, config) };
}

export const IF_ELSE_ACTION = ACTION_IF_ELSE;
